// What happens when the session cookie has run out while the app is open.
//
// The _shell guard only asks the API "am I signed in?" on a full page load, and
// it caches the answer. So once the cookie expires mid-work every request gets a
// 401 while the app still believes you are signed in — and every save showed a
// bare "Actie mislukt", which no amount of retrying fixes (2026-11-04, nine
// failed saves on AVS-2026-00140).
//
// Any 401 now flips one app-wide flag. The shell shows a notice, an open dialog
// says so in place of its generic error, and the next navigation goes to /login.
// Signing in again happens in a popup, so a half-filled dialog stays as it is
// and the user simply presses Opslaan again afterwards. The popup goes through
// the full Entra flow: nothing here extends a session on its own.

import { useSyncExternalStore } from "react";
import type { QueryClient } from "@tanstack/react-query";
import { ApiError } from "./api";
import { authMode, fetchSession, sessionQueryKey } from "./auth";

let expired = false;
const listeners = new Set<() => void>();

function setExpired(value: boolean): void {
  if (expired === value) return;
  expired = value;
  for (const l of listeners) l();
}

export function isSessionExpired(): boolean {
  return expired;
}

export function useSessionExpired(): boolean {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => expired,
  );
}

export function isUnauthorized(err: unknown): boolean {
  return err instanceof ApiError && err.status === 401;
}

/** Called from the QueryCache/MutationCache: any 401 means the session is gone. */
export function noteApiError(err: unknown): void {
  if (isUnauthorized(err)) setExpired(true);
}

/** The path the popup lands on after Entra; it only closes itself. */
export const SESSION_RENEWED_PATH = "/session-renewed";

function currentPath(): string {
  return window.location.pathname + window.location.search + window.location.hash;
}

/**
 * Sign in again without leaving the page. Opens the Entra flow in a popup; when
 * that closes, check the session and, if it is back, refetch what failed. A
 * blocked popup falls back to a full redirect back to this page — the dialog is
 * lost then, but the user is no longer stuck.
 */
export function reauthenticate(queryClient: QueryClient): void {
  if (authMode() === "mock") {
    window.location.href = `/login?redirect=${encodeURIComponent(currentPath())}`;
    return;
  }
  const popup = window.open(
    `/api/v1/auth/login?redirect=${encodeURIComponent(SESSION_RENEWED_PATH)}`,
    "avds-relogin",
    "popup,width=520,height=680",
  );
  if (!popup) {
    window.location.href = `/api/v1/auth/login?redirect=${encodeURIComponent(currentPath())}`;
    return;
  }
  const timer = window.setInterval(() => {
    if (!popup.closed) return;
    window.clearInterval(timer);
    void queryClient
      .fetchQuery({ queryKey: sessionQueryKey, queryFn: fetchSession, staleTime: 0 })
      .then((session) => {
        if (!session) return; // popup closed without signing in
        setExpired(false);
        void queryClient.invalidateQueries();
      });
  }, 500);
}
